'use client';

import Box from '@enlighten/design-system/dist/Box';
import Stack from '@enlighten/design-system/dist/Stack';
import { useTheme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import Image, { type StaticImageData } from 'next/image';
import React from 'react';
import Slider from 'react-slick';

export interface CardCarouselItem {
	thumbnail: string | StaticImageData;
	title: string;
	description?: string;
	chip?: React.ReactNode;
}

interface CardCarouselProps {
	items: CardCarouselItem[];
}

const CardCarousel: React.FC<CardCarouselProps> = ({ items }) => {
	const theme = useTheme();
	const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down('sm'));

	return (
		<Stack width="100%" maxWidth="1392px" gap="24px">
			<Box
				sx={{
					'.slick-list': {
						margin: { xs: '0 -20px', md: '0 -24px' },
					},
					'.slick-slide > div': {
						padding: { xs: '0 10px', md: '0 12px' },
					},
					'.slick-dots': {
						position: 'inherit',
						'li button:before': {
							fontSize: 12,
						},
					},
				}}
			>
				<Slider slidesToShow={isMobileOrSmaller ? 1 : 2} slidesToScroll={1} infinite={false} arrows={false} dots>
					{items.map((item, slideIndex) => (
						<Stack key={slideIndex} borderRadius="16px" overflow="hidden" bgcolor="common.white" border="1px solid" borderColor="grey.300" sx={{ display: 'flex!important' }}>
							<Box
								flex="1"
								position="relative"
								sx={{
									'& img': {
										width: '100%',
										height: { xs: '168px', md: '384px' },
									},
								}}
							>
								<Image src={item.thumbnail} alt="" width={0} height={0} />
								{item.chip && (
									<Box position="absolute" bottom={{ xs: '20px', md: '24px' }} left={{ xs: '20px', md: '40px' }} zIndex={1}>
										{item.chip}
									</Box>
								)}
							</Box>

							<Stack height={{ xs: '106px', md: '158px' }} gap={{ xs: '4px', md: '10px' }} padding={{ xs: '12px', md: '40px' }}>
								<Typography fontSize={{ xs: '16px', md: '28px' }} fontWeight="bold">
									{item.title}
								</Typography>
								{item.description ? (
									<Typography fontSize={{ xs: '14px', md: '20px' }} color="#535659">
										{item.description}
									</Typography>
								) : null}
							</Stack>
						</Stack>
					))}
				</Slider>
			</Box>
		</Stack>
	);
};

export default CardCarousel;
